import React, { useEffect, useRef } from 'react';
import { ArrowRight, Sparkles, TrendingUp, Shield, Globe2, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';

const Hero = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles = [];
    const particleCount = 50;

    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 2 + 1;
        this.speedX = Math.random() * 0.5 - 0.25;
        this.speedY = Math.random() * 0.5 - 0.25;
        this.opacity = Math.random() * 0.5 + 0.2;
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;

        if (this.x > canvas.width) this.x = 0;
        if (this.x < 0) this.x = canvas.width;
        if (this.y > canvas.height) this.y = 0;
        if (this.y < 0) this.y = canvas.height;
      }

      draw() {
        ctx.fillStyle = `rgba(201, 169, 97, ${this.opacity})`;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }

    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      particles.forEach((particle, index) => {
        particle.update();
        particle.draw();

        // Connect particles
        particles.slice(index + 1).forEach(otherParticle => {
          const dx = particle.x - otherParticle.x;
          const dy = particle.y - otherParticle.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 150) {
            ctx.strokeStyle = `rgba(201, 169, 97, ${0.1 * (1 - distance / 150)})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(otherParticle.x, otherParticle.y);
            ctx.stroke();
          }
        });
      });

      requestAnimationFrame(animate);
    }

    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#1a2837]">
      {/* Video Background */}
      <div className="absolute inset-0 z-0">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover opacity-30"
        >
          <source src="https://cdn.coverr.co/videos/coverr-aerial-view-of-cargo-ships-in-a-port-6562/1080p.mp4" type="video/mp4" />
          <source src="https://cdn.coverr.co/videos/coverr-containers-on-a-cargo-ship-9193/1080p.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a2837]/90 via-[#2D5F7F]/85 to-[#1a2837]/90"></div>
      </div>

      {/* Animated Canvas Background */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 z-0"
      />

      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 z-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(201, 169, 97, 0.1) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(201, 169, 97, 0.1) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      {/* Floating Orbs */}
      <div className="absolute top-1/4 left-10 w-96 h-96 bg-[#C9A961]/10 rounded-full blur-[100px] animate-pulse-slow"></div>
      <div className="absolute bottom-1/4 right-10 w-[500px] h-[500px] bg-[#2D5F7F]/20 rounded-full blur-[120px] animate-pulse-slow delay-700"></div>
      <div className="absolute top-1/2 left-1/2 w-[600px] h-[600px] bg-[#C9A961]/5 rounded-full blur-[150px] animate-pulse-slow"></div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-6 py-32">
        <div className="max-w-7xl mx-auto">
          {/* Top Badge */}
          <div className="flex justify-center mb-8 animate-fade-in">
            <div className="group relative inline-flex items-center gap-3 bg-white/5 backdrop-blur-xl border border-[#C9A961]/30 rounded-full px-6 py-3 hover:border-[#C9A961]/50 transition-all">
              <div className="w-2 h-2 bg-[#C9A961] rounded-full animate-pulse"></div>
              <Sparkles className="w-5 h-5 text-[#C9A961]" />
              <span className="text-white/90 text-sm font-semibold tracking-wide">Conectando Brasil ao Mundo desde 2010</span>
              <div className="absolute inset-0 bg-[#C9A961]/5 rounded-full blur-xl group-hover:bg-[#C9A961]/10 transition-all"></div>
            </div>
          </div>

          {/* Main Heading - Ultra Bold */}
          <div className="text-center mb-10">
            <h1 className="text-4xl md:text-7xl lg:text-8xl font-black text-white mb-6 leading-tight tracking-tight">
              <span className="block mb-2 md:mb-4">Conectando sua</span>
              <span className="block mb-2 md:mb-4">empresa ao</span>
              <span className="block">
                <span className="relative inline-block">
                  <span className="relative z-10 bg-gradient-to-r from-[#C9A961] via-[#f4e4b8] to-[#C9A961] bg-clip-text text-transparent animate-gradient">
                    Mercado Global
                  </span>
                  <div className="absolute -bottom-4 left-0 right-0 h-4 bg-gradient-to-r from-transparent via-[#C9A961]/40 to-transparent blur-xl"></div>
                </span>
              </span>
            </h1>
          </div>

          {/* Subtitle */}
          <p className="text-2xl md:text-3xl text-gray-300 mb-16 max-w-5xl mx-auto leading-relaxed text-center font-light">
            Transforme sua operação global com{' '}
            <span className="font-bold text-white">excelência comprovada</span>,{' '}
            <span className="font-bold text-white">transparência total</span> e{' '}
            <span className="font-bold text-white">eficiência máxima</span>
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-24">
            <Button
              onClick={() => scrollToSection('contact')}
              className="group relative bg-gradient-to-r from-[#C9A961] via-[#d4b76e] to-[#C9A961] hover:from-[#b89850] hover:via-[#C9A961] hover:to-[#b89850] text-gray-900 font-bold px-12 py-8 text-xl rounded-2xl shadow-2xl hover:shadow-[#C9A961]/50 transition-all transform hover:scale-105 overflow-hidden"
            >
              <span className="relative z-10 flex items-center gap-3">
                Começar Agora
                <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform" />
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
            </Button>
            <Button
              onClick={() => scrollToSection('services')}
              className="group border-2 border-white/20 bg-white/5 backdrop-blur-xl text-white hover:bg-white hover:text-gray-900 font-bold px-12 py-8 text-xl rounded-2xl transition-all hover:border-white"
            >
              <span className="flex items-center gap-3">
                Explorar Serviços
                <ChevronDown className="w-6 h-6 group-hover:translate-y-1 transition-transform" />
              </span>
            </Button>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {[
              { icon: Globe2, title: 'Rede Global', stat: '50+', desc: 'Países atendidos', gradient: 'from-blue-500/20 to-cyan-500/20' },
              { icon: TrendingUp, title: 'Eficiência', stat: '+35%', desc: 'Redução de custos', gradient: 'from-[#C9A961]/20 to-[#f4e4b8]/20' },
              { icon: Shield, title: 'Segurança', stat: '100%', desc: 'Compliance garantido', gradient: 'from-emerald-500/20 to-green-500/20' }
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  className="group relative bg-white/5 backdrop-blur-2xl border border-white/10 rounded-3xl p-8 hover:bg-white/10 hover:border-[#C9A961]/30 transition-all hover:-translate-y-3 hover:shadow-2xl hover:shadow-[#C9A961]/10"
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${item.gradient} rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity`}></div>
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-6">
                      <Icon className="w-16 h-16 text-[#C9A961]" strokeWidth={1.5} />
                      <div className="text-5xl font-black text-white/20 group-hover:text-[#C9A961]/30 transition-colors">
                        {item.stat}
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-gray-400 text-sm">{item.desc}</p>
                  </div>
                  <div className="absolute top-0 right-0 w-32 h-32 bg-[#C9A961]/5 rounded-full blur-3xl group-hover:bg-[#C9A961]/10 transition-all"></div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Scroll Indicator - More Prominent */}
      <div className="absolute bottom-16 left-1/2 transform -translate-x-1/2 z-20">
        <div className="flex flex-col items-center gap-3 animate-bounce">
          <span className="text-gray-400 text-sm font-medium uppercase tracking-wider">Explore</span>
          <div className="w-8 h-12 border-2 border-[#C9A961]/50 rounded-full flex items-start justify-center p-2">
            <div className="w-2 h-4 bg-[#C9A961] rounded-full animate-pulse"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
